import { Component, computed, output, signal } from '@angular/core';
import { RidAlgorithmNormaleVoet } from '../../components/rid-algorithm-normale-voet/rid-algorithm-normale-voet';
import { RidAlgorithmStommeVoet } from '../../components/rid-algorithm-stomme-voet/rid-algorithm-stomme-voet';
import { RidButton } from '../../components/rid-button/rid-button';
import { RidMechanicBackground } from '../../components/rid-mechanic-background/rid-mechanic-background';
import { RidMechanicContent } from '../../components/rid-mechanic-content/rid-mechanic-content';
import { RidMechanicWrapper } from '../../components/rid-mechanic-wrapper/rid-mechanic-wrapper';
import { RidSyllabicKeyboard } from '../../components/rid-syllabic-keyboard/rid-syllabic-keyboard';
import {
  isTyped,
  keyboardGroups,
  RidSyllabicKeyboardUtils,
  TKey,
  TKeyboardLayout,
  TKeyValue,
} from '../../components/rid-syllabic-keyboard/rid-syllabic-keyboard.utils';
import { RidTextInput } from '../../components/rid-text-input/rid-text-input';
import { RidTopBar } from '../../components/rid-top-bar/rid-top-bar';
import { IconBackspace } from '../../icons/i-backspace';
import { IconEnter } from '../../icons/i-enter';
import { Dubbelzetter, IKeyboard, KortsteVorm, Langermaken, Tekendief } from './rid-algorithm.rules';

@Component({
  selector: 'rid-algorithm',
  imports: [
    RidMechanicBackground,
    RidTopBar,
    RidMechanicWrapper,
    RidMechanicContent,
    RidTextInput,
    IconBackspace,
    RidButton,
    IconEnter,
    RidSyllabicKeyboard,
    RidAlgorithmNormaleVoet,
    RidAlgorithmStommeVoet,
  ],
  templateUrl: './rid-algorithm.html',
  host: {
    class: 'block ',
  },
})
export class RidAlgorithm {
  phase = signal<'KLANKVOET' | 'TEXT'>('KLANKVOET');
  contents = signal<
    (
      | { klankvoet: '<'; content: TKey[]; hasDubbelzetter: boolean }
      | { klankvoet: '>'; content: TKey[]; hasDubbelzetter: boolean; length: number; isLengthConfirmed: boolean }
    )[]
  >([]);
  index = signal(0);
  layout = computed<TKeyboardLayout>(() => {
    const phase = this.phase();
    if (phase === 'KLANKVOET') {
      return {
        KLINKERS: 'HIDDEN',
        LANGE_KLINKERS: 'HIDDEN',
        KORTE_LANGE_KLINKERS: 'HIDDEN',
        SPACE: 'HIDDEN',
        DUBBELE_KLINKERS: 'HIDDEN',
        DRIEDUBBELE_KLINKERS: 'HIDDEN',
        VIERDUBBELE_KLINKERS: 'HIDDEN',
        MEDEKLINKERS: 'HIDDEN',
        EXOTEN: 'HIDDEN',
        SJWAS: 'HIDDEN',
        KLANKVOET: 'ENABLED',
        RULE: 'HIDDEN',
      };
    } else {
      const klankvoet = this.contents()[this.index()].klankvoet;
      const isNormal = klankvoet === '>';
      if (isNormal) {
        return {
          KLINKERS: 'ENABLED',
          LANGE_KLINKERS: 'ENABLED',
          KORTE_LANGE_KLINKERS: 'HIDDEN',
          SPACE: 'HIDDEN',
          DUBBELE_KLINKERS: 'ENABLED',
          DRIEDUBBELE_KLINKERS: 'ENABLED',
          VIERDUBBELE_KLINKERS: 'ENABLED',
          MEDEKLINKERS: 'ENABLED',
          EXOTEN: 'HIDDEN',
          SJWAS: 'DISABLED',
          KLANKVOET: 'HIDDEN',
          RULE: 'ENABLED',
        };
      } else {
        return {
          KLINKERS: 'DISABLED',
          LANGE_KLINKERS: 'DISABLED',
          KORTE_LANGE_KLINKERS: 'HIDDEN',
          SPACE: 'HIDDEN',
          DUBBELE_KLINKERS: 'DISABLED',
          DRIEDUBBELE_KLINKERS: 'DISABLED',
          VIERDUBBELE_KLINKERS: 'DISABLED',
          MEDEKLINKERS: 'ENABLED',
          EXOTEN: 'HIDDEN',
          SJWAS: 'ENABLED',
          KLANKVOET: 'HIDDEN',
          RULE: 'DISABLED',
        };
      }
    }

    throw new Error('Invalid state');
  });
  baseMask = computed(() => {
    const currentVoet = this.contents().at(this.index());
    if (currentVoet && currentVoet.hasDubbelzetter && currentVoet.content.length === 0) {
      return [...keyboardGroups.MEDEKLINKERS].map((k) => k.value);
    }

    return [
      ...keyboardGroups.KLANKVOET,
      ...keyboardGroups.KLINKERS,
      ...keyboardGroups.LANGE_KLINKERS,
      ...keyboardGroups.DUBBELE_KLINKERS,
      ...keyboardGroups.DRIEDUBBELE_KLINKERS,
      ...keyboardGroups.VIERDUBBELE_KLINKERS,
      ...keyboardGroups.MEDEKLINKERS,
      ...keyboardGroups.SJWAS,
    ].map((k) => k.value);
  });
  isValidVoetenInput = computed(() => {
    const contents = this.contents();
    return contents.length > 0 && contents.some((c) => c.klankvoet === '>'); // NOTE: at least one normal klankvoet is required to proceed to text input phase
  });
  canOkay = computed(() => {
    const phase = this.phase();
    if (phase === 'KLANKVOET') {
      return this.isValidVoetenInput();
    } else {
      if (this.interceptor()) return false; // if waiting for input, can't proceed

      const contents = this.contents();
      const index = this.index();
      const klankvoet = contents[index];
      const isNormalVoet = klankvoet.klankvoet === '>';
      if (isNormalVoet) {
        if (!klankvoet.isLengthConfirmed) return klankvoet.length > 0;
        else return klankvoet.content.length === klankvoet.length;
      } else {
        return klankvoet.content.length > 0;
      }
    }
  });
  canBackspace = computed(() => {
    const phase = this.phase();
    if (phase === 'KLANKVOET') {
      return this.contents().length > 0;
    } else {
      const contents = this.contents();
      const index = this.index();
      const klankvoet = contents[index];
      return klankvoet.content.length > 0;
    }
  });
  mask = signal<TKeyValue[] | undefined>(undefined);
  highlightMask = signal<TKeyValue[] | undefined>(undefined);

  interceptor = signal<((key: TKey) => void) | undefined>(undefined);

  finished = output<void>();

  protected Array = Array;

  keyboard: IKeyboard = {
    insert: (key: TKey) => {
      const contents = this.contents();
      const klankvoet = contents[this.index()];
      klankvoet.content.push(key);
      this.contents.set([...contents]);
    },
    highlight: (keys: TKeyValue[]) => {
      this.highlightMask.set(keys);
    },
    clearHighlight: () => {
      this.highlightMask.set(undefined);
    },
    getInput: async <T extends TKeyValue>(keys: T[]): Promise<T> => {
      this.mask.set(keys);
      const result = await new Promise<T>((resolve) => {
        this.interceptor.set((key: TKey) => {
          resolve(key.value as T);
          this.interceptor.set(undefined);
        });
      });
      this.mask.set(undefined);
      return result;
    },
    setDubbelzetter: (active: boolean) => {
      const contents = this.contents();
      const index = this.index();
      contents[index + 1].hasDubbelzetter = active;
      this.contents.set([...contents]);
    },
  };

  onTyped(key: TKey) {
    const interceptor = this.interceptor();
    if (interceptor) {
      interceptor(key);
      return;
    }

    const phase = this.phase();
    if (phase === 'KLANKVOET') {
      if (key.type === 'KLANKVOET') {
        const klankvoet = key.value;
        this.contents.update((contents) => [
          ...contents,
          klankvoet === '>'
            ? { klankvoet: '>', content: [], hasDubbelzetter: false, length: 0, isLengthConfirmed: false }
            : { klankvoet: '<', content: [], hasDubbelzetter: false },
        ]);
      }
    } else {
      const contents = this.contents();
      const index = this.index();
      const klankvoet = contents[index];

      if (klankvoet.hasDubbelzetter && klankvoet.content.length === 0) {
        klankvoet.content.push({ type: 'KLANK', value: `${key.value}${key.value}` as any });
        this.contents.set([...contents]);
        return;
      }

      const isNormalVoet = klankvoet.klankvoet === '>';
      if (isNormalVoet) {
        const isLastKlankInVoet = klankvoet.content.length === klankvoet.length - 1;
        const isLangeKlank = RidSyllabicKeyboardUtils.getKeyGroupName(key) === 'LANGE_KLINKERS';
        if (isLastKlankInVoet && isLangeKlank && isTyped(key.value, ['aa', 'ee', 'oo', 'uu'])) {
          new Tekendief(this.keyboard).apply(key.value);
          return;
        }

        const isSecondToLastKlank = klankvoet.content.length === klankvoet.length - 2;
        if (
          ((isLastKlankInVoet || isSecondToLastKlank) && isTyped(key.value, ['au', 'ou', 'auw', 'ouw', 'ch', 'g'])) ||
          (isSecondToLastKlank && isTyped(key.value, ['ng', 'nk']))
        ) {
          new KortsteVorm(this.keyboard).apply(key.value);
          return;
        }

        if (isLastKlankInVoet && isTyped(key.value, ['t', 'p'])) {
          new Langermaken(this.keyboard).apply(key.value);
          return;
        }

        const isLastKlankvoet = index === contents.length - 1;
        if (isLastKlankInVoet && isTyped(key.value, ['a', 'e', 'i', 'o', 'u']) && !isLastKlankvoet) {
          new Dubbelzetter(this.keyboard).apply(key.value);
          return;
        }
      }

      klankvoet.content.push(key);
      this.contents.set([...contents]);
    }
  }

  pop() {
    const phase = this.phase();
    if (phase === 'KLANKVOET') {
      this.contents.update((contents) => contents.slice(0, -1));
    } else {
      const contents = this.contents();
      const index = this.index();
      const klankvoet = contents[index];
      klankvoet.content.pop();
      const nextKlankvoet = contents.at(index + 1);
      if (nextKlankvoet) nextKlankvoet.hasDubbelzetter = false; //  remove the (potential) dubbelzetter state from the next klankvoet
      this.contents.set([...contents]);
    }
  }

  setLength(length: number) {
    const contents = this.contents();
    const klankvoet = contents[this.index()];
    if (klankvoet.klankvoet === '>') {
      klankvoet.length = length;
      this.contents.set([...contents]);
    }
  }

  okay() {
    const phase = this.phase();
    if (phase === 'KLANKVOET') {
      this.phase.set('TEXT');
    } else {
      const klankvoet = this.contents()[this.index()];
      if (klankvoet.klankvoet === '>' && !klankvoet.isLengthConfirmed) {
        klankvoet.isLengthConfirmed = true;
        this.contents.set([...this.contents()]);
      } else if (this.index() < this.contents().length - 1) {
        this.index.set(this.index() + 1);
      } else {
        this.finish();
      }
    }
  }

  finish() {
    this.finished.emit();
  }
}
